import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import Icon from 'react-native-easy-icon';
import _, { isFinite } from 'lodash';
import {
  dispatch,
  getActivePOOrderLines,
  getPrincipalWorkspaceDetails,
  ErrorVariants,
  getSelectedCustomerDetails,
  showToast,
  useAppSelector,
  useDeleteFromActiveOrderMutation,
  useUpdateActiveOrderItemMutation,
  EditLineData,
} from '@zonofi/seller-rtk-store';
import { useGetCurrentChannelData } from '@zonofi/chats-common';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from '../helpers/responsive-native';
import { SHARED_SVGS } from '../assets';
import DeleteWarningModal from '../delete-warning-modal/delete-warning-modal';
import { colors } from '../colors/colors';
import { TogglePopUp } from '../toggle-pop-up/toggle-pop-up';
import { ThemeContext } from '../contexts/ThemeContext';
import { Strings } from '../helpers';
import { KeyboardAction, KeyboardItemMetaData } from '../helpers';

interface AddToCartProps {
  selectedOrder?: any;
  minWidth?: any;
  isDisabled?: boolean;
  defaultMOQ?: number;
  fromCart?: boolean;
  keyboardItemMetaData?: KeyboardItemMetaData;
  isQtyInfo?: boolean;
  isMapped?: boolean;
  remappedContent?: JSX.Element | null;
  customerId?: string;
  onSuccessAddedToCart?: () => void;
  onSuccessDeletedToCart?: () => void;
  updateEditableData?: (
    actionType: string,
    productVariantId: number,
    updatedLineData?: EditLineData,
    poFileLineId?: string
  ) => void;
}

enum Action {
  add = 'add',
  remove = 'remove',
  delete = 'delete',
}

export const AddToCart: React.FC<AddToCartProps> = ({
  selectedOrder,
  minWidth,
  isDisabled,
  fromCart,
  defaultMOQ,
  keyboardItemMetaData,
  isQtyInfo,
  isMapped,
  remappedContent,
  customerId = '',
  updateEditableData,
  onSuccessAddedToCart,
  onSuccessDeletedToCart,
}) => {
  const [post, response] = useUpdateActiveOrderItemMutation();
  const [deleteLine, deleteResponse] = useDeleteFromActiveOrderMutation();
  const selectedPrincipal = useAppSelector(getPrincipalWorkspaceDetails);
  const orderLines = useAppSelector(getActivePOOrderLines);
  const selectedCustomer = useAppSelector(getSelectedCustomerDetails);
  const [isEdit, setIsEdit] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const selectedChannel = useGetCurrentChannelData();
  const warningMessage = useMemo(
    () => <Text>Are you sure you want to delete this product?</Text>,
    []
  );

  const [moqError, setMoqError] = useState(false);

  const inputRef = useRef(null) as any;

  const product = useMemo(() => {
    const selectedOrderId = selectedOrder.id || selectedOrder.product?.sku;
    if (
      fromCart &&
      selectedOrder?.modeOfOrder &&
      selectedOrder.modeOfOrder !== 'manual'
    ) {
      const { quantity, modeOfOrder, orderId, orderLineId } = selectedOrder;
      return {
        quantity,
        importSource: fromCart ? modeOfOrder : 'manual',
        orderId,
        orderLineId,
      };
    }
    const filteredProduct = orderLines?.find((line) => {
      const isMatching =
        line?.productVariantId === selectedOrderId ||
        line?.product?.sku === selectedOrderId ||
        line?.product?.productVariantId === selectedOrderId;

      if (fromCart) {
        return (
          isMatching &&
          (line.orderLineId === selectedOrder.orderLineId ||
            line.id === selectedOrder.poFileLineId ||
            line.id === selectedOrder.orderLineId)
        );
      } else {
        return isMatching && line.modeOfOrder === 'manual';
      }
    });

    setLoader(false);

    if (filteredProduct) {
      const { quantity, modeOfOrder, orderId, orderLineId } = filteredProduct;
      return {
        quantity,
        importSource: fromCart ? modeOfOrder : 'manual',
        orderId,
        orderLineId,
      };
    } else {
      return {
        quantity: 0,
        importSource: 'manual',
      };
    }
  }, [fromCart, orderLines, selectedOrder]);

  const [quantity, setQuantity] = useState<number>(Number(product.quantity));
  const currentTheme = useContext(ThemeContext);
  const { theme } = currentTheme;

  const getDefaultValueIfNotExists = (
    value: number | undefined,
    defaultValue: number
  ) => {
    return _.isUndefined(value) || value === 0 ? defaultValue : value;
  };
  const qtyMultiplier = useMemo(
    () => getDefaultValueIfNotExists(selectedOrder?.minOrderQty, 1),
    [selectedOrder]
  );

  const minOrderQty = useMemo(
    () =>
      Math.ceil(
        getDefaultValueIfNotExists(selectedOrder?.minOrderQty, 1) /
          qtyMultiplier
      ) * qtyMultiplier,
    [qtyMultiplier, selectedOrder]
  );

  const baseUOM = useMemo(
    () =>
      !_.isUndefined(selectedOrder?.baseUom) &&
      !_.isEmpty(selectedOrder?.baseUom)
        ? selectedOrder?.baseUom
        : 'Qty',
    [selectedOrder]
  );

  const isLoading = useMemo(
    () => response.isLoading || deleteResponse?.isLoading,
    [response, deleteResponse]
  );

  const isKeyboardActiveRow = useMemo(() => {
    if (keyboardItemMetaData) {
      return (
        keyboardItemMetaData?.keyboardActiveIndex ===
        keyboardItemMetaData?.itemIndex
      );
    }
    return false;
  }, [keyboardItemMetaData]);

  const sendPostRequest = useCallback(
    (quantity: number, isDispatchPOFile = false) => {
      setLoader(true);
      !isLoading &&
        post({
          sellerWorkspaceId: selectedPrincipal?.sellerId,
          isDispatchPOFile,
          body: {
            customerId:
              selectedPrincipal?.inviteId ||
              selectedCustomer?.id ||
              selectedChannel?.customerId ||
              customerId,
            sellerWorkspaceId: selectedPrincipal?.sellerId,
            poFileId: selectedOrder.poFileId,
            source: 'platform',
            lines: [
              {
                productVariantId: selectedOrder?.id,
                quantity: quantity,
                operator: quantity > product.quantity ? 'add' : 'minus',
                ...(selectedOrder?.poFileLineId !== undefined && {
                  poFileLineId: selectedOrder.poFileLineId,
                }),
              },
            ],
          },
        });
    },
    [isLoading, selectedPrincipal, selectedOrder, product, selectedCustomer]
  );

  const deleteRequest = useCallback(() => {
    setLoader(true);
    !isLoading &&
      deleteLine({
        sellerWorkspaceId: selectedPrincipal?.sellerId,
        customerId:
          selectedPrincipal?.inviteId ||
          selectedCustomer?.id ||
          selectedChannel?.customerId ||
          customerId,
        importSource: product.importSource,
        poFileId: selectedOrder.poFileId,
        lines: [
          { orderId: product?.orderId, orderLineId: product?.orderLineId },
        ],
      });
  }, [deleteLine, isLoading, selectedPrincipal, product, selectedCustomer]);

  const updateQuantity = useCallback(
    (value: number) => {
      setQuantity(value);
      if (value < qtyMultiplier) {
        setMoqError(true);
        setIsEdit(false);
        setQuantity(product.quantity?.toString());
      } else if (value !== product.quantity) {
        sendPostRequest(value);
      }
    },
    [sendPostRequest, qtyMultiplier]
  );

  const getQuantity = useCallback(
    (action: Action) => {
      switch (action) {
        case Action.add:
          return Number(product.quantity) + 1;
        case Action.delete:
          return Number(product.quantity) - 1;
        case Action.remove:
          return 0;
        default:
          throw new Error('invalid action');
      }
    },
    [product.quantity, qtyMultiplier]
  );

  const changeQuantityToCart = useCallback(
    (action: Action) => {
      const quantity = getQuantity(action);
      if (action === Action.remove) {
        deleteRequest();
      } else if (quantity < minOrderQty) {
        setMoqError(true);
      } else {
        sendPostRequest(quantity);
      }
    },
    [deleteRequest, getQuantity, minOrderQty, sendPostRequest]
  );

  const renderProductQtyInfo = () => {
    const systemModifiedQty =
      Math.ceil(selectedOrder?.uploadedQty / minOrderQty) * minOrderQty;
    const uploadedQty =
      selectedOrder?.uploadedQty > 0 ? selectedOrder?.uploadedQty : '-';
    if (
      product?.quantity > systemModifiedQty ||
      product?.quantity < systemModifiedQty
    ) {
      return (
        <TogglePopUp
          heading={Strings.ORDER_QUANTITY_MODIFIED}
          value={`Order quantity has been modified from ${uploadedQty} to ${product?.quantity}.`}
          headingStyles={{ color: '#808285', fontSize: 10, marginTop: 6 }}
          color={'#4481B4'}
          contentStyles={{
            fontSize: 15,
            color: theme.text.primary,
            fontFamily: theme.fontConfig.regular.fontFamily,
            width: 240,
            marginTop: 8,
          }}
        />
      );
    } else if (systemModifiedQty > selectedOrder?.uploadedQty) {
      return (
        <TogglePopUp
          heading={Strings.ORDER_QUANTITY_MODIFIED}
          value={`System automatically adjusted the quantity from ${uploadedQty} to ${product?.quantity} as the product failed to meet the minimum order quantity.`}
          headingStyles={{ color: '#808285', fontSize: 10, marginTop: 6 }}
          color={'#4481B4'}
          contentStyles={{
            fontSize: 15,
            color: theme.text.primary,
            fontFamily: theme.fontConfig.regular.fontFamily,
            width: 240,
            marginTop: 8,
          }}
        />
      );
    } else return <View style={{ width: 16 }} />;
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent.key === 'Enter') {
      e.nativeEvent.preventDefault();
      updateQuantity(Number(quantity));
      inputRef.current?.blur();
      keyboardItemMetaData?.keyboardCallback(
        'escape',
        keyboardItemMetaData?.itemIndex
      );
    } else if (e.nativeEvent.key === 'ArrowUp') {
      e.nativeEvent.preventDefault();
      changeQuantityToCart(Action.add);
    } else if (e.nativeEvent.key === 'ArrowDown') {
      e.nativeEvent.preventDefault();
      if (!(Number(product.quantity) - qtyMultiplier < minOrderQty)) {
        changeQuantityToCart(Action.delete);
      } else {
        setMoqError(true);
      }
    } else if (e.nativeEvent.key === 'Escape') {
      e.nativeEvent.preventDefault();
      inputRef.current?.blur();
      keyboardItemMetaData?.keyboardCallback(
        'escape',
        keyboardItemMetaData?.itemIndex
      );
    } else if (e.shiftKey && e.nativeEvent.key === 'Tab') {
      e.nativeEvent.preventDefault();
      inputRef.current?.blur();
      keyboardItemMetaData?.keyboardCallback(
        'escape',
        keyboardItemMetaData?.itemIndex
      );
    } else if (e.nativeEvent.key === 'Tab') {
      e.nativeEvent.preventDefault();
      inputRef.current?.blur();
      keyboardItemMetaData?.keyboardCallback(
        'escape',
        keyboardItemMetaData?.itemIndex
      );
    } else if (e.nativeEvent.key === 'Delete') {
      e.nativeEvent.preventDefault();
      changeQuantityToCart(Action.remove);
      keyboardItemMetaData?.keyboardCallback(
        'delete',
        keyboardItemMetaData?.itemIndex
      );
    }
  };

  const dispatchToast = (
    message: string,
    status: string,
    type: ErrorVariants
  ) => {
    dispatch(
      showToast({
        canDisplay: true,
        message,
        responseStatus: status,
        type,
      })
    );
  };

  useEffect(() => {
    if (response?.isSuccess) {
      updateEditableData &&
        updateEditableData('addToCartLine', selectedOrder?.productVariantId);
      onSuccessAddedToCart && onSuccessAddedToCart();
    }
    if (response?.isSuccess && response?.data?.errors) {
      if (!_.isEmpty(response?.data?.errors)) {
        dispatchToast(
          `${response?.data?.errors[0]?.msg}`,
          'WARNING',
          ErrorVariants.WARNING
        );
      }
    } else if (response?.isError) {
      if (
        (response?.error as any)?.data?.statusCode === 422 &&
        (response?.error as any)?.data?.message?.message ===
          'Quantity cannot be below the Minimum Order Quantity (MOQ)'
      ) {
        setMoqError(true);
        setQuantity(product.quantity?.toString());
      }
      dispatchToast(
        `${(response?.error as any)?.data?.message?.message}`,
        'WARNING',
        ErrorVariants.WARNING
      );
    }
    setLoader(false);
    setOpenWarningModal(false);
  }, [response]);

  useEffect(() => {
    if (deleteResponse?.isSuccess && deleteResponse?.data) {
      if (!_.isEmpty(deleteResponse?.data?.error)) {
        dispatchToast(
          `${deleteResponse?.data?.error[0]}`,
          'WARNING',
          ErrorVariants.WARNING
        );
      } else {
        onSuccessDeletedToCart && onSuccessDeletedToCart();
        dispatchToast(
          'Product removed from the cart successfully!',
          'SUCCESS',
          ErrorVariants.SUCCESS
        );
        keyboardItemMetaData?.keyboardCallback(
          'delete',
          keyboardItemMetaData?.itemIndex
        );
      }
    } else if (deleteResponse?.isError) {
      dispatchToast(
        `${(deleteResponse?.error as any)?.data?.message}`,
        'WARNING',
        ErrorVariants.WARNING
      );
    }
    setOpenWarningModal(false);
  }, [deleteResponse]);

  useEffect(() => {
    if (isKeyboardActiveRow) {
      if (keyboardItemMetaData?.keyboardAction === KeyboardAction.Enter) {
        if (product.quantity === 0) {
          sendPostRequest(minOrderQty);
        } else {
          inputRef.current?.focus();
          setIsEdit(true);
        }
      }
      if (keyboardItemMetaData?.keyboardAction === KeyboardAction.Delete) {
        if (product.quantity !== 0) {
          changeQuantityToCart(Action.remove);
        }
      }
    }
  }, [keyboardItemMetaData]);

  useEffect(() => {
    if (product.quantity !== quantity) setQuantity(product.quantity);
  }, [product.quantity]);

  useEffect(() => {
    if (moqError) {
      setTimeout(() => setMoqError(false), 2300);
    }
  }, [moqError]);

  return product.quantity === 0 || _.isUndefined(product.quantity) ? (
    <View style={{ alignItems: 'flex-start' }}>
      {response?.isLoading ? (
        <ActivityIndicator color={theme?.text.primary} size={20} />
      ) : (
        <TouchableOpacity
          onPress={async () => {
            sendPostRequest(defaultMOQ ? defaultMOQ : minOrderQty, fromCart);
          }}
          disabled={isDisabled ? isDisabled : false}
          style={[
            { padding: 10 },
            isKeyboardActiveRow && {
              backgroundColor: theme?.table.underline,
              borderRadius: 6,
            },
          ]}
        >
          <Text
            style={[
              theme.fontConfig.medium,
              {
                fontSize: 14,
                color: isKeyboardActiveRow
                  ? 'white'
                  : !_.isUndefined(isDisabled) && isDisabled
                    ? '#808285'
                    : theme?.table.underline,
              },
            ]}
          >
            Add to Cart
            <Icon
              type={'font-awesome'}
              name={'arrow-circle-right'}
              color={
                isKeyboardActiveRow
                  ? 'white'
                  : !_.isUndefined(isDisabled) && isDisabled
                    ? '#808285'
                    : theme?.table.underline
              }
              size={12}
              style={styles.addToCartIcon}
            />
          </Text>
        </TouchableOpacity>
      )}
    </View>
  ) : (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {isQtyInfo && renderProductQtyInfo()}
      <View style={styles.cartContainer}>
        <Text style={[theme.fontConfig.regular, styles.uomText]}>
          {!response?.isLoading && baseUOM}
        </Text>
        {isLoading && (
          <ActivityIndicator
            color={theme?.text.primary}
            size={16}
            style={{ marginRight: 5, minWidth: 15 }}
          />
        )}
        <View
          style={[
            {
              ...styles.counterContainer,
              minWidth: minWidth,
              borderColor: moqError ? theme.status.danger : '#DFE0E0',
            },
            // {
          ]}
        >
          <TouchableOpacity
            disabled={isLoading}
            onPress={() => changeQuantityToCart(Action.delete)}
          >
            <Icon
              type="antdesign"
              name="minus"
              color={
                moqError
                  ? theme.status.danger
                  : isLoading
                    ? colors.cadetGrey.color
                    : theme?.table.underline
              }
              size={20}
              style={[
                styles.incrementButtons,
                moqError
                  ? {
                      borderColor: theme.status.danger,
                      borderWidth: 1,
                      borderRadius: 6,
                      backgroundColor: theme.status.background.danger,
                    }
                  : {
                      backgroundColor: 'rgba(68, 129, 180, 0.05)',
                      borderWidth: 1,
                      borderColor: 'transparent',
                    },
              ]}
            />
          </TouchableOpacity>
          <TextInput
            ref={inputRef}
            onChangeText={(value) => {
              setIsEdit(true);
              if (isFinite(Number(value)) && Number(value) >= 0) {
                setQuantity(Number(value));
              }
            }}
            onBlur={() => {
              if (quantity !== product.quantity?.toString()) {
                updateQuantity(quantity);
              }
            }}
            editable={!isLoading}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsEdit(true)}
            value={isEdit ? quantity : product.quantity?.toString()}
            style={[
              styles.inputBox,
              {
                color: isLoading ? colors.cadetGrey.color : theme?.text.primary,
                backgroundColor: theme?.addToCart?.input,
                fontSize: 14,
              },
            ]}
          />
          <TouchableOpacity
            disabled={isLoading}
            onPress={() => changeQuantityToCart(Action.add)}
          >
            <Icon
              type="antdesign"
              name="plus"
              size={20}
              color={
                isLoading ? colors.cadetGrey.color : theme?.table.underline
              }
              style={[
                styles.incrementButtons,
                moqError
                  ? {
                      borderColor: theme.status.danger,
                      borderWidth: 1,
                      borderRadius: 6,
                      backgroundColor: theme.status.background.danger,
                    }
                  : {
                      backgroundColor: 'rgba(68, 129, 180, 0.05)',
                      borderWidth: 1,
                      borderColor: 'transparent',
                    },
              ]}
            />
          </TouchableOpacity>
        </View>
        {fromCart && isMapped && remappedContent && remappedContent}
        <TouchableOpacity
          onPress={() => setOpenWarningModal(true)}
          style={{ marginLeft: 5, marginRight: 5, alignItems: 'center' }}
        >
          <SHARED_SVGS.DeleteIcon height={28} width={28} color={'#808285'} />
        </TouchableOpacity>
      </View>
      {moqError && (
        <View
          style={{
            backgroundColor: theme.status.danger,
            borderRadius: 4,
            paddingHorizontal: 10,
            paddingVertical: 4,
            alignItems: 'center',
            position: 'absolute',
            top: '80%',
          }}
        >
          <Text
            style={{
              ...theme.fontConfig.semiBold,
              fontSize: 10,
              color: colors.white.color,
              textAlign: 'center',
            }}
          >
            Qty can’t be below product MOQ
          </Text>
        </View>
      )}
      <DeleteWarningModal
        open={openWarningModal}
        warningMessage={warningMessage}
        onClose={() => setOpenWarningModal(false)}
        deleteHandler={() => {
          setOpenWarningModal(false);
          changeQuantityToCart(Action.remove);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  addToCartIcon: {
    marginLeft: 8,
    marginTop: 1,
  },
  cartContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    minWidth: 35,
    marginLeft: 5,
  },
  infoIcon: {
    // cursor: 'pointer',
    zIndex: 9999,
  },
  popupContainer: {
    backgroundColor: '#34353e',
    padding: 15,
    borderRadius: 4,
  },
  popupTitle: {
    margin: 0,
    color: '#9295a5',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  popupText: {
    margin: 0,
    fontSize: 14,
    paddingTop: 6,
  },
  checkIcon: {
    marginLeft: -20,
  },
  inputBox: {
    width: wp(3.5),
    height: hp(3.5),
    display: 'flex',
    padding: 3,
    textAlign: 'center',
    alignSelf: 'center',
  },
  limitText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.lightGrey.color,
    fontStyle: 'italic',
    textAlign: 'center',
    minHeight: 15,
  },
  counterContainer: {
    flexDirection: 'row',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  incrementButtons: {
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  uomText: {
    color: '#BFC0C2',
    fontSize: 14,
    textAlign: 'center',
    marginRight: 5,
  },
});

export default AddToCart;
